import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosInstance } from "axios";
import {
    Alert,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    MenuItem,
    Radio,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { useNotification } from "providers/Notification";
import { IsUnauthorizedError } from "providers/Login";
import ConfirmDialog from "components/ConfirmDialog";

import Panel from "../components/Panel";
import Gauge from "../components/Gauge";
import { bytes, count, relativeTimeMs } from "../components/format";
import {
    FIELD_STATUS_LABEL,
    INDEX_STATE_LABEL,
    MigrationFieldPlan,
    MigrationPreview,
    SearchIndexEntry,
    SearchIndexStream,
    STREAM_LABEL,
    migrationHandled,
    migrationPercent,
    missingFrom,
    recordsPhrase,
} from "../../_components/searchIndexTypes";
import {
    cancelSearchIndexMigration,
    createSearchIndex,
    deleteSearchIndex,
    getMigrationPreview,
    listSearchIndexes,
    rethrottleSearchIndexMigration,
    setSearchReadIndex,
    startSearchIndexMigration,
} from "../../_components/searchIndexesApi";

// Poll fast enough for a progress bar to feel live while a copy is running,
// and fall back to the Services page cadence when nothing is happening.
const MIGRATION_POLL_MS = 3_000;
const IDLE_POLL_MS = 15_000;

// migrationPlan works out what a copy would be, for one stream: the index
// that is missing history, the indices that hold some, and which of them is
// selected. The parent needs it to fetch a preview and the section needs it to
// render, so it lives in one place.
function migrationPlan(stream: SearchIndexStream, chosen?: string) {
    // A copy can only land in the index that is receiving records; anything
    // else would be missing whatever arrived while the copy ran.
    const inUse = stream.indices.find((i) => i.state === "active");
    // And it is only worth offering while some other index still holds
    // records that have not been copied across.
    const candidates = inUse ? missingFrom(stream, inUse.name) : [];
    const target = candidates.length > 0 ? inUse : undefined;
    const source = candidates.some((c) => c.name === chosen)
        ? chosen as string
        : (candidates[0]?.name || "");
    return { target, candidates, source };
}

// previewKey identifies one source/destination pair for caching.
function previewKey(stream: string, from: string, to: string): string {
    return `${stream}:${from}->${to}`;
}

// A pending delete, and whether it needs the index name typed back.
interface PendingDelete {
    stream: string
    name: string
    requiresConfirmation: boolean
    docs: number
    // docsKnown false means the confirmation is being asked for because
    // nobody can say what is in there, which is a different sentence.
    docsKnown: boolean
}

// rateSample tracks progress between polls so a documents-per-second figure
// and an ETA can be derived without the server computing them.
interface RateSample {
    handled: number
    at: number
    perSecond: number
}

export default function IndexesPanel({ req }: { req: AxiosInstance }) {
    const notification = useNotification();

    const [streams, setStreams] = useState<SearchIndexStream[] | null>(null);
    const [busy, setBusy] = useState<string>("");
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
    const [confirmText, setConfirmText] = useState("");
    const [sources, setSources] = useState<Record<string, string>>({});
    const [previews, setPreviews] = useState<Record<string, MigrationPreview | null>>({});
    const [previewLoading, setPreviewLoading] = useState<string>("");

    const reqRef = useRef(req);
    reqRef.current = req;

    // Previews are asked for once per source/destination pair; the answer only
    // changes when a mapping does, which needs a deployment.
    const requested = useRef<Set<string>>(new Set());

    const rates = useRef<Record<string, RateSample>>({});

    const anyRunning = !!streams?.some((s) => s.migration?.state === "running");

    // Fold one stream's new state back in, so a mutation's response renders
    // without waiting for the next poll.
    const replaceStream = useCallback((updated: SearchIndexStream) => {
        setStreams((current) => (current || []).map((s) => (s.stream === updated.stream ? updated : s)));
    }, []);

    useEffect(() => {
        let active = true;
        let inFlight = false;

        const load = async () => {
            const r = reqRef.current;
            // Skip if a previous poll is still pending, so a slow older
            // response cannot overwrite newer progress.
            if (!r || inFlight) return;
            inFlight = true;
            try {
                const fetched = await listSearchIndexes(r);
                if (!active) return;

                // Derive a rate from the delta since the last sample.
                const now = Date.now();
                fetched.forEach((s) => {
                    const m = s.migration;
                    if (!m || m.state !== "running") {
                        delete rates.current[s.stream];
                        return;
                    }
                    const handled = migrationHandled(m);
                    const previous = rates.current[s.stream];
                    let perSecond = previous?.perSecond || 0;
                    if (previous && now > previous.at) {
                        const elapsed = (now - previous.at) / 1000;
                        const delta = handled - previous.handled;
                        if (elapsed > 0 && delta >= 0) {
                            // Smooth it, or the figure jumps around between polls.
                            const instant = delta / elapsed;
                            perSecond = previous.perSecond ? (previous.perSecond * 0.6) + (instant * 0.4) : instant;
                        }
                    }
                    rates.current[s.stream] = { handled, at: now, perSecond };
                });

                setStreams(fetched);
            } catch (err) {
                if (IsUnauthorizedError(err)) {
                    return; //GetAxios already dropped the session and redirected
                }
                if (!active) return;
                // A polled panel must not shout on every failed tick; render
                // what we have and let the next poll recover.
                setStreams((current) => current || []);
            } finally {
                inFlight = false;
            }
        };

        load();
        const handle = window.setInterval(load, anyRunning ? MIGRATION_POLL_MS : IDLE_POLL_MS);
        return () => { active = false; window.clearInterval(handle); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyRunning]);

    const plans = (streams || []).map((stream) => ({
        stream,
        ...migrationPlan(stream, sources[stream.stream]),
    }));
    const pairs = plans
        .filter((plan) => plan.source && plan.target)
        .map((plan) => previewKey(plan.stream.stream, plan.source, plan.target!.name));

    // Fetch the comparison for whatever pair is on screen, so the operator
    // sees what a copy would do before choosing to run one.
    useEffect(() => {
        const r = reqRef.current;
        if (!r) return;
        let active = true;

        // Only ever clear our own key: an older request finishing after the
        // operator picked a different pair would otherwise switch off the
        // newer pair's spinner.
        const clearLoading = (key: string) =>
            setPreviewLoading((current) => (current === key ? "" : current));

        (async () => {
            for (const key of pairs) {
                if (requested.current.has(key)) continue;
                requested.current.add(key);

                const [stream, pair] = key.split(":");
                const [from, to] = pair.split("->");
                setPreviewLoading(key);
                try {
                    const preview = await getMigrationPreview(r, stream, from, to);
                    if (!active) {
                        // Never answered, so it must not count as asked, or
                        // remounting the panel would show this pair blank for
                        // good.
                        requested.current.delete(key);
                        return;
                    }
                    setPreviews((current) => ({ ...current, [key]: preview }));
                } catch (err) {
                    // Not asked-and-answered, so let the pair be asked again.
                    // The effect only re-runs when the set of pairs on screen
                    // changes, so this is a retry when the operator selects
                    // this pair again — not a retry on a timer.
                    requested.current.delete(key);
                    if (IsUnauthorizedError(err)) {
                        return; //GetAxios already dropped the session and redirected
                    }
                    if (!active) return;
                    // Meanwhile the panel says the comparison is unavailable
                    // rather than sitting on a spinner.
                    setPreviews((current) => ({ ...current, [key]: null }));
                } finally {
                    clearLoading(key);
                }
            }
        })();

        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pairs.join("|")]);

    // run wraps a mutation with a busy key, a toast on failure, and folding
    // the response back into state.
    const run = async (key: string, action: () => Promise<SearchIndexStream>, success?: string) => {
        setBusy(key);
        try {
            replaceStream(await action());
            if (success) {
                notification.Display({ title: "Success", message: success, type: "success" });
            }
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            const anyErr = err as { response?: { data?: { message?: string } } };
            notification.Display({
                title: "Error",
                message: anyErr.response?.data?.message || "Request failed",
                type: "error",
            });
        } finally {
            setBusy("");
        }
    };

    if (streams === null) {
        return <Panel title="Indices"><Typography color="text.secondary">Loading…</Typography></Panel>;
    }

    return <>
        {plans.map(({ stream, target, candidates, source }) => (
            <StreamSection
                key={stream.stream}
                stream={stream}
                busy={busy}
                rate={rates.current[stream.stream]?.perSecond}
                source={source}
                candidates={candidates}
                target={target}
                preview={target && source ? previews[previewKey(stream.stream, source, target.name)] : undefined}
                previewLoading={!!target && !!source
                    && previewLoading === previewKey(stream.stream, source, target.name)}
                onSourceChange={(from) => setSources((s) => ({ ...s, [stream.stream]: from }))}
                onCreate={() => run(`create:${stream.stream}`,
                    () => createSearchIndex(reqRef.current!, stream.stream),
                    `Now using ${stream.available}.`)}
                onDeleteRequest={(entry) => {
                    setConfirmText("");
                    setPendingDelete({
                        stream: stream.stream,
                        name: entry.name,
                        // Records may exist nowhere else, so destroying any
                        // that remain is worth typing the name for — and an
                        // index nobody can count may be holding all of them,
                        // which is why the server refuses that one too.
                        requiresConfirmation: entry.docs > 0 || !entry.docsKnown,
                        docs: entry.docs,
                        docsKnown: entry.docsKnown,
                    });
                }}
                onSetRead={(name) => run(`read:${name}`,
                    () => setSearchReadIndex(reqRef.current!, stream.stream, name),
                    `Reading ${name}.`)}
                onMigrate={(from, to) => run(`migrate:${stream.stream}`,
                    () => startSearchIndexMigration(reqRef.current!, stream.stream, from, to),
                    "Copy started.")}
                onRethrottle={(rps) => run(`throttle:${stream.stream}`,
                    () => rethrottleSearchIndexMigration(reqRef.current!, stream.stream, rps))}
                onCancel={() => run(`cancel:${stream.stream}`,
                    () => cancelSearchIndexMigration(reqRef.current!, stream.stream),
                    "Copy cancelled.")}
            />
        ))}

        <DeleteIndexDialog
            pending={pendingDelete}
            confirmText={confirmText}
            onConfirmTextChange={setConfirmText}
            loading={busy.startsWith("delete:")}
            onConfirm={async () => {
                const target = pendingDelete;
                if (!target) return;
                await run(`delete:${target.name}`,
                    () => deleteSearchIndex(reqRef.current!, target.stream, target.name,
                        target.requiresConfirmation ? target.name : undefined),
                    `${target.name} deleted.`);
                setPendingDelete(null);
            }}
            onCancel={() => setPendingDelete(null)}
        />
    </>;
}

interface StreamSectionProps {
    stream: SearchIndexStream
    busy: string
    rate?: number
    source: string
    candidates: SearchIndexEntry[]
    target?: SearchIndexEntry
    // preview is undefined until asked for, null when the comparison could
    // not be fetched.
    preview?: MigrationPreview | null
    previewLoading: boolean
    onSourceChange: (from: string) => void
    onCreate: () => void
    onDeleteRequest: (entry: SearchIndexEntry) => void
    onSetRead: (name: string) => void
    onMigrate: (from: string, to: string) => void
    onRethrottle: (rps: number) => void
    onCancel: () => void
}

function StreamSection(props: StreamSectionProps) {
    const { stream, target, candidates, source } = props;
    const running = stream.migration?.state === "running";
    const activeCount = stream.indices.filter((i) => i.state === "active").length;
    const degraded = stream.readSubstrFields.length < stream.fullSubstrFields.length;

    // inUse is the current version, once it is actually in use. writeTarget is
    // whichever index is taking the records, which is not the same thing when
    // the current version could not be created — the case the warning below
    // exists for.
    const inUse = stream.indices.find((i) => i.name === stream.current && i.state === "active");
    const writeTarget = stream.indices.find((i) => i.state === "active");
    const readEntry = stream.indices.find((i) => i.name === stream.readIndex);
    // Records the index being read does not have. Derived from counts and
    // finished copies, so it disappears by itself once a copy succeeds.
    const elsewhere = missingFrom(stream, stream.readIndex);
    const holdsHistory = elsewhere[0];

    return <Panel title={`${STREAM_LABEL[stream.stream] || stream.stream} indices`}>
        {stream.available && (
            <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                    <LoadingButton
                        variant="contained"
                        size="small"
                        loading={props.busy === `create:${stream.stream}`}
                        onClick={props.onCreate}
                    >
                        Create and use
                    </LoadingButton>
                }
            >
                <b>{stream.available}</b> could not be created — the cluster may not allow it.
                {/* The index taking the records, not the one being read: an
                    operator looking at a retired index would otherwise be
                    told their records are going there. */}
                {writeTarget
                    ? <> Records still go to <b>{writeTarget.name}</b>.</>
                    : <> No index is receiving records.</>}
            </Alert>
        )}

        {/* Which index the log is coming from, always. */}
        {readEntry && (readEntry.state === "frozen"
            ? <Alert severity="warning" sx={{ mb: 2 }}>
                Reading <b>{readEntry.name}</b> — stopped {relativeTimeMs(readEntry.disabledAt)}.
                {inUse ? <> New records go to <b>{inUse.name}</b>.</> : null}
            </Alert>
            : holdsHistory
                ? <Alert severity="info" sx={{ mb: 2 }}>
                    Reading <b>{readEntry.name}</b> — new records only.
                    {" "}{recordsPhrase(holdsHistory)} in <b>{holdsHistory.name}</b>.
                    {" "}{running ? "Copying them across now." : "Switch below to read them, or copy them across."}
                </Alert>
                : <Alert severity="success" sx={{ mb: 2 }}>
                    Reading <b>{readEntry.name}</b> — every record is here.
                </Alert>)}

        {degraded && (
            <Alert severity="warning" sx={{ mb: 2 }}>
                <b>{stream.readIndex}</b> predates the substring index, so filters match exact
                values only. <b>{stream.current}</b> has it.
            </Alert>
        )}

        <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Tooltip title="One index at a time"><span>Read</span></Tooltip>
                        </TableCell>
                        <TableCell>Index</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell align="right">Records</TableCell>
                        <TableCell align="right">Size</TableCell>
                        <TableCell>Last write</TableCell>
                        <TableCell>Covers</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {stream.indices.map((entry) => (
                        <IndexRow
                            key={entry.name}
                            entry={entry}
                            stream={stream}
                            busy={props.busy}
                            activeCount={activeCount}
                            running={running}
                            onDeleteRequest={props.onDeleteRequest}
                            onSetRead={props.onSetRead}
                        />
                    ))}
                </TableBody>
            </Table>
        </Box>

        <MigrationBlock
            stream={stream}
            busy={props.busy}
            rate={props.rate}
            source={source}
            candidates={candidates}
            target={target}
            preview={props.preview}
            previewLoading={props.previewLoading}
            onSourceChange={props.onSourceChange}
            onMigrate={props.onMigrate}
            onRethrottle={props.onRethrottle}
            onCancel={props.onCancel}
        />
    </Panel>;
}

interface IndexRowProps {
    entry: SearchIndexEntry
    stream: SearchIndexStream
    busy: string
    activeCount: number
    running: boolean
    onDeleteRequest: (entry: SearchIndexEntry) => void
    onSetRead: (name: string) => void
}

function IndexRow(props: IndexRowProps) {
    const { entry, stream } = props;
    const isRead = stream.readIndex === entry.name;
    const touchedByMigration = props.running
        && (stream.migration?.from === entry.name || stream.migration?.to === entry.name);

    // Guarded actions render disabled with the reason, never hidden: the
    // operator should see that an action exists and why it is unavailable.
    // Reading is never blocked — every registered index holds its records, and
    // reaching a retired one is the whole point of the column.
    const readHint = entry.state === "frozen" && !isRead ? "Read this index" : "";
    const deleteReason = touchedByMigration
        ? "A copy is using this index"
        : isRead
            ? "Switch reads elsewhere first"
            : entry.state === "active" && props.activeCount <= 1
                ? "This index is receiving records"
                : "";

    return <TableRow hover>
        <TableCell padding="checkbox">
            <Tooltip title={readHint}>
                <span>
                    <Radio
                        size="small"
                        checked={isRead}
                        disabled={props.busy === `read:${entry.name}`}
                        onChange={() => props.onSetRead(entry.name)}
                        inputProps={{ "aria-label": `Read from ${entry.name}` }}
                    />
                </span>
            </Tooltip>
        </TableCell>

        <TableCell>
            <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="body2">{entry.name}</Typography>
                {entry.current && <Chip size="small" label="Current" variant="outlined" />}
            </Stack>
        </TableCell>

        <TableCell>
            <Chip
                size="small"
                label={INDEX_STATE_LABEL[entry.state]}
                color={entry.state === "active" ? "success" : "default"}
                variant={entry.state === "active" ? "filled" : "outlined"}
            />
        </TableCell>

        {/* An unreported count renders as unknown, not as zero: it sits one
            cell away from Delete, and "0" is what would make destroying it
            look free. */}
        <TableCell align="right">{count(entry.docsKnown ? entry.docs : undefined)}</TableCell>
        <TableCell align="right">{bytes(entry.sizeBytes)}</TableCell>

        {/* Last write, not "stopped": when it stopped is what Covers says,
            and saying it twice cost this row a column its header never had. */}
        <TableCell>
            <Typography variant="body2" color="text.secondary">
                {relativeTimeMs(entry.lastWriteAt)}
            </Typography>
        </TableCell>

        <TableCell>
            <Typography variant="body2" color="text.secondary">
                {entry.enabledAt ? relativeTimeMs(entry.enabledAt) : "—"}
                {entry.state === "frozen" && entry.disabledAt
                    ? ` to ${relativeTimeMs(entry.disabledAt)}`
                    : entry.state === "active" ? " onwards" : ""}
            </Typography>
        </TableCell>

        <TableCell align="right">
            <Stack direction="row" gap={1} justifyContent="flex-end">
                <Tooltip title={deleteReason}>
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={!!deleteReason}
                            onClick={() => props.onDeleteRequest(entry)}
                        >
                            Delete
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
        </TableCell>
    </TableRow>;
}

interface MigrationBlockProps {
    stream: SearchIndexStream
    busy: string
    rate?: number
    source: string
    candidates: SearchIndexEntry[]
    target?: SearchIndexEntry
    preview?: MigrationPreview | null
    previewLoading: boolean
    onSourceChange: (from: string) => void
    onMigrate: (from: string, to: string) => void
    onRethrottle: (rps: number) => void
    onCancel: () => void
}

function MigrationBlock(props: MigrationBlockProps) {
    const { stream, target } = props;
    const migration = stream.migration;
    const running = migration?.state === "running";
    const inUse = stream.indices.find((i) => i.name === stream.current && i.state === "active");

    if (running && migration) {
        const handled = migrationHandled(migration);
        const percent = migrationPercent(migration);
        const remaining = Math.max(0, migration.total - handled);
        const eta = props.rate && props.rate > 0 ? remaining / props.rate : undefined;

        return <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
                Copying <b>{migration.from}</b> → <b>{migration.to}</b>
            </Typography>
            <Gauge label={`${percent}% copied`} value={handled} max={migration.total} />
            <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    {count(handled)} of {count(migration.total)}
                </Typography>
                {props.rate !== undefined && props.rate > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        {Math.round(props.rate).toLocaleString("en-US")}/s
                    </Typography>
                )}
                {eta !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                        {formatEta(eta)} left
                    </Typography>
                )}
                {migration.versionConflicts > 0 && (
                    <Tooltip title="Already in the destination — skipped, not overwritten">
                        <Typography variant="caption" color="text.secondary">
                            {count(migration.versionConflicts)} skipped
                        </Typography>
                    </Tooltip>
                )}
            </Stack>

            <Stack direction="row" gap={2} alignItems="center" sx={{ mt: 2 }} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">Rate limit</Typography>
                <Select
                    size="small"
                    value={String(migration.throttleRps)}
                    onChange={(e) => props.onRethrottle(Number(e.target.value))}
                    disabled={props.busy === `throttle:${stream.stream}`}
                >
                    <MenuItem value="250">250/s</MenuItem>
                    <MenuItem value="1000">1,000/s</MenuItem>
                    <MenuItem value="5000">5,000/s</MenuItem>
                    <MenuItem value="-1">No limit</MenuItem>
                    {!([250, 1000, 5000, -1] as number[]).includes(migration.throttleRps) && (
                        <MenuItem value={String(migration.throttleRps)}>{migration.throttleRps}/s</MenuItem>
                    )}
                </Select>
                <Typography variant="caption" color="text.secondary">
                    Lower it if the cluster struggles. The copy keeps going.
                </Typography>
                <LoadingButton
                    size="small"
                    variant="outlined"
                    color="error"
                    loading={props.busy === `cancel:${stream.stream}`}
                    onClick={props.onCancel}
                >
                    Cancel
                </LoadingButton>
            </Stack>
        </Box>;
    }

    return <Box sx={{ mt: 3 }}>
        {migration?.state === "failed" && (
            <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    <b>{migration.from}</b> → <b>{migration.to}</b> stopped at
                    {" "}{count(migrationHandled(migration))} of {count(migration.total)}. Running it
                    again is safe and resumes.
                </Typography>
                {migration.error && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1 }}>{migration.error}</Typography>
                )}
                {(migration.failures || []).slice(0, 3).map((failure, i) => (
                    <Typography key={i} variant="caption" sx={{ display: "block", mt: 0.5 }}>{failure}</Typography>
                ))}
            </Alert>
        )}

        {migration?.state === "succeeded" && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Copied {count(migrationHandled(migration))} records {migration.from} → {migration.to},
                {" "}{relativeTimeMs(migration.finishedAt)}.
            </Typography>
        )}

        {migration?.state === "cancelled" && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Cancelled {relativeTimeMs(migration.finishedAt)} after
                {" "}{count(migrationHandled(migration))} records. Starting it again resumes.
            </Typography>
        )}

        {!target && (
            <Typography variant="body2" color="text.secondary">
                {!inUse
                    ? "No index is currently receiving records."
                    : "Nothing to copy — every record is in the index in use."}
            </Typography>
        )}

        {target && props.candidates.length > 0 && (
            <>
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2">Copy into <b>{target.name}</b> from</Typography>
                    <Select
                        size="small"
                        value={props.source}
                        onChange={(e) => props.onSourceChange(String(e.target.value))}
                    >
                        {props.candidates.map((candidate) => (
                            <MenuItem key={candidate.name} value={candidate.name}>
                                {candidate.name} · {count(candidate.docs)}
                            </MenuItem>
                        ))}
                    </Select>
                    <LoadingButton
                        variant="contained"
                        loading={props.busy === `migrate:${stream.stream}`}
                        onClick={() => props.onMigrate(props.source, target.name)}
                    >
                        {migration?.state === "failed" || migration?.state === "cancelled" ? "Run again" : "Copy"}
                    </LoadingButton>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 200px" }}>
                        Optional. Nothing is deleted.
                    </Typography>
                </Stack>

                <MigrationPreviewBlock preview={props.preview} loading={props.previewLoading} />
            </>
        )}
    </Box>;
}

// fieldHint is the specifics behind a status label, for a hover.
function fieldHint(field: MigrationFieldPlan): string {
    switch (field.status) {
        case "conflict":
            return `${field.sourceType} here, ${field.targetType} there`;
        case "added":
            return "No old record has this field";
        case "enriched":
            return `Gains ${(field.gained || []).map((g) => `.${g}`).join(", ")}`;
        case "undeclared":
            return "Not in the new mapping; copied as it arrives";
        default:
            return "";
    }
}

// MigrationPreviewBlock is what makes the copy a decision rather than a leap:
// how much would move, what the new mapping improves, and what it cannot fill
// in for records written before it existed.
function MigrationPreviewBlock(props: { preview?: MigrationPreview | null, loading: boolean }) {
    const { preview } = props;

    if (props.loading && !preview) {
        return <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Checking…
        </Typography>;
    }
    if (preview === undefined) return null;
    if (preview === null) {
        return <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            Field comparison could not be loaded. The copy still works.
        </Typography>;
    }
    if (preview.mappingUnavailable) {
        return <Alert severity="info" sx={{ mt: 2 }}>
            A mapping could not be read, so there is no field comparison. The copy still moves every
            record not already in <b>{preview.to}</b>.
        </Alert>;
    }

    const notable = preview.fields.filter((f) => f.status !== "carried");

    return <Box sx={{ mt: 2.5 }}>
        <Typography variant="subtitle2" gutterBottom>What moves</Typography>

        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip size="small" variant="outlined"
                label={`${count(preview.sourceDocs)} records${preview.wholeIndex ? "" : " before the hand-over"}`} />
            <Chip size="small" variant="outlined" label={bytes(preview.sourceSizeBytes)} />
            <Chip size="small" variant="outlined" color="success"
                label={`${preview.carried + preview.enriched} fields kept`} />
            {preview.enriched > 0 && (
                <Chip size="small" variant="outlined" color="success"
                    label={`${preview.enriched} improved`} />
            )}
            {preview.added > 0 && (
                <Chip size="small" variant="outlined" color="warning"
                    label={`${preview.added} left empty`} />
            )}
            {preview.undeclared > 0 && (
                <Chip size="small" variant="outlined" label={`${preview.undeclared} unmapped`} />
            )}
            {preview.conflicts > 0 && (
                <Chip size="small" variant="outlined" color="error"
                    label={`${preview.conflicts} need attention`} />
            )}
        </Stack>

        {preview.blocking && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
                Some fields are typed differently. Those values may be rejected; the rest still
                moves.
            </Alert>
        )}

        {notable.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
                Both mappings match — the copy is exact.
            </Typography>
        ) : (
            <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>After the copy</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notable.map((field) => (
                            <TableRow key={field.field} hover>
                                <TableCell>
                                    <Typography variant="body2" component="span">{field.field}</Typography>
                                </TableCell>
                                <TableCell>
                                    {/* The label is the answer; the specifics are on hover for
                                        whoever wants them. */}
                                    <Tooltip title={fieldHint(field)}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={FIELD_STATUS_LABEL[field.status]}
                                            color={field.status === "conflict"
                                                ? "error"
                                                : field.status === "added"
                                                    ? "warning"
                                                    : field.status === "enriched"
                                                        ? "success"
                                                        : "default"}
                                        />
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {preview.carried > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        {preview.carried} more {preview.carried === 1 ? "field" : "fields"} unchanged.
                    </Typography>
                )}
            </Box>
        )}
    </Box>;
}

// DeleteIndexDialog asks twice when the index still holds records: once as a
// dialog, once by typing its name.
function DeleteIndexDialog(props: {
    pending: PendingDelete | null
    confirmText: string
    onConfirmTextChange: (v: string) => void
    loading: boolean
    onConfirm: () => void
    onCancel: () => void
}) {
    const pending = props.pending;
    if (!pending) return null;

    if (!pending.requiresConfirmation) {
        return <ConfirmDialog
            open
            title="Delete this index?"
            message={<>Deletes <b>{pending.name}</b> permanently. This cannot be undone.</>}
            confirmLabel="Delete"
            confirmColor="error"
            loading={props.loading}
            onConfirm={props.onConfirm}
            onCancel={props.onCancel}
        />;
    }

    const matches = props.confirmText.trim() === pending.name;

    return <Dialog open>
        <DialogTitle>
            {pending.docsKnown ? `Delete ${count(pending.docs)} records?` : "Delete an index of unknown size?"}
        </DialogTitle>
        <DialogContent>
            <DialogContentText component="div">
                {pending.docsKnown
                    ? <><b>{pending.name}</b> still holds records. They may exist nowhere else, and
                        this cannot be undone.</>
                    : <>The cluster did not report how many records <b>{pending.name}</b> holds — it
                        may be closed, or its shards unavailable. It could be empty or it could be
                        the only copy of the older log, and this cannot be undone.</>}
            </DialogContentText>
            <TextField
                fullWidth
                size="small"
                sx={{ mt: 2 }}
                label="Type the index name"
                value={props.confirmText}
                onChange={(e) => props.onConfirmTextChange(e.target.value)}
                inputProps={{ "aria-label": "Type the index name to confirm" }}
            />
        </DialogContent>
        <DialogActions>
            <Button id="cancel-delete-button" variant="outlined" onClick={props.onCancel}>Cancel</Button>
            <LoadingButton
                id="confirm-delete-button"
                variant="contained"
                color="error"
                disabled={!matches}
                loading={props.loading}
                onClick={props.onConfirm}
            >
                Delete Permanently
            </LoadingButton>
        </DialogActions>
    </Dialog>;
}

function formatEta(seconds: number): string {
    if (seconds < 60) return `${Math.ceil(seconds)}s`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.ceil((seconds - (hours * 3600)) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}
